import React, { useState, useRef, Suspense, useCallback, useMemo } from "react";
import * as survey from "@aidkitorg/types/lib/survey";
import { getDistroBrowserIncompatibility } from "../../Util";
import { useLocation } from "react-router-dom";
import { usePost } from "../../API";
import { Button, useToast, Select, SelectOptionType } from "@aidkitorg/component-library";
import { useAsyncEffect } from "../../Hooks/AsyncEffect";

const Distro = React.lazy(() => import("@aidkitorg/typesheets/lib/distroeditor"));

const blankTemplate: survey.EditableMessageTemplate = {
  kind: "Message Template",
  name: "",
  version: 0,
  description: "",
  instructions: []
};

type StoredTemplate = survey.MessageTemplate & {
  underlyingInstructions?: survey.Instruction[];
  underlyingOutput?: survey.OutputFormat;
};

type TemplateOption = {
  label: string;
  value: string;
  template: StoredTemplate;
};

function convertStoredTemplateToEditable(storedTemplate: StoredTemplate): survey.EditableMessageTemplate {
  const { underlyingInstructions, underlyingOutput, ...templateFields } = storedTemplate;
  const template: survey.EditableMessageTemplate = {
    ...templateFields,
    kind: 'Message Template',
  };

  if (underlyingInstructions) {
    template.instructions = underlyingInstructions;
  }

  if (underlyingOutput) {
    template.output = underlyingOutput;
  }

  return template;
}


export function MessageTemplateConfig() {
  const findTemplateRequest = usePost('/program/admin/prompts/list');
  const [loadedTemplate, setLoadedTemplate] = useState<survey.EditableMessageTemplate | null>(null);
  const location = useLocation();

  useAsyncEffect(async () => {
    const queryParams = new URLSearchParams(location.search)
    const name = queryParams.get('name');
    const version = queryParams.get('version');

    if (name) {
      const { items: templates } = await findTemplateRequest({ name, version: version ? parseInt(version) : undefined });
      const existingTemplate = templates?.[templates?.length - 1];
      if (existingTemplate) {
        const storedTemplate = { ...existingTemplate, kind: 'Message Template' } as StoredTemplate;
        setLoadedTemplate(convertStoredTemplateToEditable(storedTemplate));
        return;
      }
    }

    setLoadedTemplate(blankTemplate);
  }, []);

  if (!loadedTemplate) return <div>Loading...</div>;

  return <InnerMessageTemplateConfig initial={loadedTemplate} />;
}

export function InnerMessageTemplateConfig(props: { initial: survey.EditableMessageTemplate }) {
  const [messageTemplate, setMessageTemplate] = useState<survey.EditableMessageTemplate>(props.initial);
  const [latestVersion, setLatestVersion] = useState(props.initial.version);
  const [availableTemplates, setAvailableTemplates] = useState<TemplateOption[]>([]);
  const [selectedTemplateKey, setSelectedTemplateKey] = useState<string>('');

  const distroRef = useRef<React.ComponentRef<typeof Distro>>(null);

  const createTemplateRequest = usePost('/program/admin/prompts/create');
  const findTemplateRequest = usePost('/program/admin/prompts/list');
  const scanTemplatesRequest = usePost('/program/admin/prompts/scan');

  const { toast } = useToast();

  useAsyncEffect(async () => {
    const { items } = await scanTemplatesRequest({});

    const templatesByName = new Map();
    for (const template of items || []) {
      const existing = templatesByName.get(template.name);
      if (!existing || template.version > existing.version) {
        templatesByName.set(template.name, template);
      }
    }

    const options: TemplateOption[] = Array.from(templatesByName.values()).map(template => ({
      label: `${template.name} (v${template.version})`,
      value: `${template.name}:${template.version}`,
      template: { ...template, kind: 'Message Template' as const } as StoredTemplate
    }));

    setAvailableTemplates(options);
  }, []);

  useAsyncEffect(async () => {
    const name = messageTemplate.name?.trim();
    if (!name) {
      setLatestVersion(0);
      return;
    }

    const templates = await findTemplateRequest({ name });
    const latest = (templates.items ?? [])
      .map(item => item.version)
      .filter(v => typeof v === 'number')
      .sort((a, b) => b - a)[0] ?? 0;

    setLatestVersion(latest);
  }, [messageTemplate.name]);

  const saveTemplate = useCallback(async () => {
    const oldVersion = messageTemplate.version ?? 0;
    const newVersion = (latestVersion ?? oldVersion) + 1;
    const newTemplate = { ...messageTemplate, version: newVersion };

    if (oldVersion > 0) {
      await createTemplateRequest({ item: newTemplate });
      toast({
        description: `created new template version: v${oldVersion} => v${newVersion}`,
        autoClose: true,
        duration: 1_500
      });
    } else {
      await createTemplateRequest({ item: newTemplate });
      toast({
        description: 'created new template!',
        autoClose: true,
        duration: 1_500
      });
    }
    setMessageTemplate(newTemplate);
    setLatestVersion(newTemplate.version);
  }, [createTemplateRequest, messageTemplate, latestVersion, toast]);


  const isDerivedTemplate = useMemo(() => {
    return messageTemplate.instructions.some(instruction =>
      instruction.kind === 'Message Template Reference'
    ) || (messageTemplate.output?.type === 'template');
  }, [messageTemplate.instructions, messageTemplate.output]);

  const incompatible = getDistroBrowserIncompatibility();
  if (incompatible) return incompatible;

  return (
    <div className="bg-gray-100 h-full p-4">
      <h1>Message Template Management</h1>

      <div className="mt-4 bg-white shadow ring-1 ring-black ring-opacity-5 md:rounded-lg space-y-4 p-4">
        <h2 className="text-lg font-semibold">Template Editor</h2>

        <div className="bg-blue-50 border border-blue-200 rounded-md p-3 text-sm">
          <div className="flex items-center gap-2">
            <div className="w-2 h-2 bg-blue-500 rounded-full"></div>
            <strong>Message Template References</strong>
          </div>
          <p className="mt-1 text-blue-700">
            You can reference other message templates in your instructions and output formats using "Message Template Reference" instructions.
          </p>
          <div className="mt-2 text-blue-600 text-xs">
            <p><strong>How to use:</strong></p>
            <ul className="list-disc list-inside mt-1 space-y-1">
              <li><strong>Instructions:</strong> Add a "Message Template Reference" instruction to include another template's content.</li>
              <li><strong>Output Format:</strong> Set output type to "template" to reference another template's output schema.</li>
            </ul>
          </div>
        </div>

        <div></div>

        <Suspense fallback={<div>Loading editor...</div>}>
          <Distro
            ref={distroRef}
            value={messageTemplate}
            types='src/survey.ts'
            llmContext={`We are using a tool to configure message templates for AI conversations.
            Message templates define the structure and instructions for AI interactions.
            
            You can reference other message templates in two ways:
            
            1. **As Instructions**: Add a "Message Template Reference" instruction anywhere in your instructions array
               - Select the template name and version to reference
               - Optionally set a role (user/system/assistant) to override the referenced instructions' roles
               - The referenced template's instructions will be inserted at that exact position
            
            2. **As Output Format**: Set the output format type to "template" 
               - Reference another template's output format
               - The referenced template's output schema will be used
            
            All template references are resolved when you save the template.
            This makes templates modular and reusable!
            
            Available templates: ${availableTemplates.map(t => t.template.name).join(', ')}`}
            name='EditableMessageTemplate'
            onChange={(v) => {
              setMessageTemplate(v)
            }}
          />
        </Suspense>

        <div className='flex flex-col items-center gap-8'>
          <div className='flex flex-row gap-4'>
            <Button
              className="ml-2 mb-2"
              variant="primary"
              onClick={saveTemplate}>
              {isDerivedTemplate
                ? `Save Derived Template (v${(latestVersion ?? messageTemplate.version) + 1})`
                : `Save New Version (v${(latestVersion ?? messageTemplate.version) + 1})`
              }
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
